import React, { useState, useEffect } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { components } from 'react-select';

import { addToast } from '~features/toast/ducks';
import * as api from '~services/api';

import headerIcon from '~images/trouble_report.svg';
import notFoundIcon from '~images/notfound.svg';

import { getOrdersByField } from '../../services/api.helpers';
import { fetchTimeInfo } from '../ducks';
import {
  setShow as setModalShow,
  setModalContents,
} from '~features/modals/ducks';

import TextareaField from '~fields/TextareaField';
import SelectField from '~fields/SelectField';
import CheckboxField from '~fields/CheckboxField';
import PhoneField from '~fields/PhoneField';
import TextField from '~fields/TextField';
import DeliveryTimeField from '~fields/DeliveryTimeField';
import SpaceSelectField from '~fields/SpaceSelectField';
import { TagField, OfficeField, ParagraphField } from '~fields';

// Mobile components
import PhotoAdder from '~components/form/PhotoAdder';
import QRCodeButton from '../components/QRCodeButton';

import {
  Button,
  Gap,
  InfoModalButton,
  FormLabel,
  Modal,
  ModalWrapper,
} from '~ui-kit';
import bookSVG from '~images/instructions.svg';

import { primaryColor } from '~theme';

const formatDevice = device => ({
  value: device.deviceId,
  label: `${device.deviceId} (${device.deviceType})`,
});

const getAppliances = id => {
  // If we are editing an existing order fetch appliances
  // based on initial identificationNumber of the device
  // const { customInitialValues } = this.props;
  // const deviceId = customInitialValues
  //   ? customInitialValues.identificationNumber
  //   : id;

  // Start search after user has typed 3 chars
  if (!id || id.length < 3) {
    return Promise.resolve([]);
  }

  return api
    .get(`data/maintenance/devices/search/?deviceid=${id}`)
    .then(appliances => (appliances || []).map(formatDevice));
};

// eslint-disable-next-line react/display-name
const noOptionsMessage = label => props =>
  (
    <components.NoOptionsMessage {...props}>
      {label || 'Ei hakutuloksia'}
    </components.NoOptionsMessage>
  );

const ApplianceMaintenanceForm = ({ formik, formId, form }) => {
  const id = formik.values.identificationNumber?.value;
  const { idNotKnown, needsTransport } = formik.values;
  const { setFieldValue, values } = formik;

  const [device, setDevice] = useState();
  const [showModal, setShowModal] = useState(false);
  const [ongoingOrder, setOngoingOrder] = useState();
  // const [maintenanceUnits, setMaintenanceUnits] = useState([]);
  const [classes, setClasses] = useState([]);

  // const defaultOrganization = useSelector(
  //   state => state.user?.data?.ordererOrganization
  // );

  const classOptions = classes.map(c => ({
    value: c.deviceClass,
    label: c.label,
    tooltip: c.description,
  }));

  const dispatch = useDispatch();

  // const munitOptions = maintenanceUnits?.map(mu => ({
  //   value: mu.unitCode,
  //   label: mu.title,
  // }));

  const handlePhotoChange = photoProps => {
    if (!photoProps) {
      setFieldValue('attachmentInput', []);
      return;
    }

    const { id, photo } = photoProps;

    setFieldValue('attachmentInput', [{ id, photo }]);
  };

  const openCantFindModal = () => {
    dispatch(
      setModalContents({
        title: 'Puuttuuko laitteesi laiteluokka-listalta?',
        content:
          'Palvelukoordinaattorimme reagoi viestiisi pikimmiten. Tee laitehuollon tilaus vasta sitten kun palvelukoordinaattori on vastannut viestiisi.',
      })
    );
    dispatch(setModalShow('TROUBLE_REPORT'));
  };

  const handleQRCode = async text => {
    try {
      const device = await api.get(`data/maintenance/devices/${text}`);
      if (device) {
        setFieldValue('identificationNumber', formatDevice(device));
        setFieldValue('idNotKnown', false);
        setDevice(device);
        setFieldValue('deviceLocation', device.deviceLocation);
        setFieldValue('deviceUnit', { value: device.organizationId });
        setFieldValue('maintenanceUnit', { value: device.maintenanceUnit });
      } else {
        dispatch(
          addToast({
            message: 'Laitetta ei löytynyt',
            type: 'ERROR',
          })
        );
      }
    } catch (e) {
      console.error('Error when fetching device from QR code', e);
      dispatch(
        addToast({
          message: 'Laitetta hakiessa tapahtui virhe',
          type: 'ERROR',
        })
      );
    }
  };

  const handleDeviceSelect = async o => {
    if (!o) return;

    try {
      const orders = await getOrdersByField(
        form.id,
        'identificationNumber',
        o.value,
        'IN_PROGRESS,DELIVERED,ORDERED,APPROVED,ACKNOWLEDGED,ASSIGNED,FAILED'
      );
      if (orders && orders.numberOfElements > 0) {
        setOngoingOrder(orders.content[0]);
        setShowModal(true);
      }
    } catch (e) {
      console.error(
        'Error in ApplianceMaintenanceForm when fetching service history:',
        e
      );
      dispatch(
        addToast({
          message: 'Laitteen huoltohistorian haku epäonnistui',
          type: 'ERROR',
        })
      );
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const [/* maintenanceUnits, */ classes] = await Promise.all([
        // api.get('data/maintenance/munit/'),
        api.get('data/maintenance/classes/'),
      ]);
      setClasses(classes);
      // setMaintenanceUnits(maintenanceUnits);
    };
    if (formId) {
      dispatch(fetchTimeInfo({}, formId));
      fetchData();
    }
  }, [formId]);

  // These ones below probably shouldn't be effects, but rather
  // onChange handlers. On the other hand editing an order is easier
  // this way.

  useEffect(() => {
    const update = async () => {
      try {
        const res = await api.get(`data/maintenance/devices/${id}`);
        setDevice(res);
        // These are only used when submitting, so no need for a label
        setFieldValue('deviceLocation', res.deviceLocation);
        setFieldValue('deviceUnit', { value: res.organizationId });
        setFieldValue('maintenanceUnit', { value: res.maintenanceUnit });
        // TODO change this to a real boolean in the future. 16.5.2025 Joni: Removed === 'true'. Jesse to clean up.
        if (res.transportable) {
          setFieldValue('needsTransport', true);
        }
      } catch (e) {
        console.error('Error when fetching device due to device ID change', e);
        dispatch(
          addToast({
            message: 'Laitetta hakiessa tapahtui virhe',
            type: 'ERROR',
          })
        );
      }
    };

    if (id) {
      setFieldValue('idNotKnown', false);
      update();
    } else {
      setDevice(null);
      setFieldValue('deviceLocation', '');
      setFieldValue('deviceUnit', null);
      setFieldValue('maintenanceUnit', null);
    }
  }, [id]);

  useEffect(() => {
    if (idNotKnown) {
      setFieldValue('identificationNumber', null);

      // if (defaultOrganization) {
      //   // In theory this office could be not part of the office list used
      //   // by the component. Hopefully this is not problematic.
      //   setFieldValue('office', {
      //     value: defaultOrganization.officeId,
      //     label: defaultOrganization.officeName,
      //   });
      // }
    }
  }, [idNotKnown]);

  // Stuff for modals
  const modalTitle = `Laitteesta on tehty ilmoitus ${
    ongoingOrder?.initiationTime
      ? moment(ongoingOrder.initiationTime).format('D.M.YYYY')
      : 'aiemmin'
  }`;
  const modalInitiator = ongoingOrder?.initiator?.name || 'Tuntematon käyttäjä';
  const modalDescription = ongoingOrder?.presentables?.find(
    p => p.name === 'additionalInfo'
  )?.value;

  return (
    <>
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <ModalWrapper
          title={modalTitle}
          headerLargeIcon={headerIcon}
          close={() => setShowModal(false)}
        >
          <ModalContent>
            <p>
              {modalInitiator} on tehnyt laitteesta seuraavan palvelupyynnön:
            </p>
            <br />
            <p>&quot;{modalDescription}&quot;</p>
            <br />
            <p>
              Mikäli asiasi koskee samaa pyyntöä, ethän tee uutta ilmoitusta.
              Kiitos!
            </p>
          </ModalContent>
        </ModalWrapper>
      </Modal>
      <FieldWrapper>
        <SelectField
          name="identificationNumber"
          fallbackLabel="Laitteen tunnusnumero"
          fallbackPlaceholder="Kirjoita esim. 01006103"
          loadOptions={getAppliances}
          async
          searchPromptText="Kirjoita vähintään kolme ensimmäistä numeroa"
          isClearable
          onChange={handleDeviceSelect}
          // onBlurResetsInput={false}
          // onBlur={() => {}} // Prevent chosen value from being overridden
          components={{
            NoOptionsMessage: noOptionsMessage(
              'Hakutermiä vastaavia laitteita ei löytynyt'
            ),
          }}
        />
        <InfoModalButton title="Mistä löydän laitteen tunnusnumeron?">
          <ModalContent>
            <p>
              Laitteen tunnusnumeron löydät laitteeseen kiinnitetystä tarrasta
              viivakoodin ja/tai QR-koodin yhteydestä.
            </p>
            <Gap height="24px" />
            <p>
              Viiva- ja QR-koodin voi mobissa lukea kätevästi myös
              Paketti-sovelluksen avulla.
            </p>
          </ModalContent>
        </InfoModalButton>
      </FieldWrapper>

      <QRCodeButton onRead={handleQRCode} />

      <FieldWrapper>
        <CheckboxField
          fallbackLabel="Laitteen tunnusnumero ei ole tiedossa"
          name="idNotKnown"
        />
      </FieldWrapper>

      <Gap height="24px" />

      {idNotKnown && <ParagraphField name="infoParagraph" />}

      {(id || idNotKnown) && (
        <>
          {idNotKnown ? (
            <>
              <FieldWrapper>
                <TagField name="deviceClass" options={classOptions} required />
                {(!classOptions || !classOptions.length) && (
                  <div>Laiteluokkia ei löytynyt</div>
                )}
                <CantFind onClick={openCantFindModal}>
                  <CantFindIcon src={notFoundIcon} /> En löydä laitettani
                </CantFind>
              </FieldWrapper>

              <Gap height="24px" />

              <FormLabel>Laitteen sijainti</FormLabel>
              <LocationContainer>
                <FieldWrapper>
                  <OfficeField name="office" flat labelWidth="80px" />
                </FieldWrapper>

                <FieldWrapper>
                  <TextField
                    name="deviceLocation"
                    fallbackLabel="Tarkempi sijainti"
                    fallbackPlaceholder="Kerro huone ym."
                    required
                  />
                </FieldWrapper>
              </LocationContainer>

              {/* <FieldWrapper>
                <SelectField
                  name="maintenanceUnit"
                  fallbackLabel="Huoltoyksikkö"
                  fallbackPlaceholder="Hae laitteen huoltava yksikkö"
                  searchPromptText="Kirjoita yksikön nimi"
                  options={munitOptions}
                  required
                />
              </FieldWrapper> */}
            </>
          ) : (
            <Details>
              <DetailsGrid>
                <Detail>Laitteen kustannuspaikka:</Detail>
                <Detail>{`${device?.costCentreCode} ${device?.costCentreTitle}`}</Detail>
                <Detail>Tarkempi sijainti:</Detail>
                <Detail>{device?.deviceLocation}</Detail>
                <Detail>Huoltoyksikkö:</Detail>
                <Detail>{device?.unitCodeName}</Detail>
              </DetailsGrid>
              {device?.instructionsUrl && (
                <>
                  <Gap height="32px" />
                  <a
                    href={`${device?.instructionsUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Instructions>
                      <span>Tutustu käyttöohjeisiin</span>
                      <Book src={bookSVG} />
                    </Instructions>
                  </a>
                </>
              )}
            </Details>
          )}

          <FieldWrapper>
            <TextareaField
              fallbackPlaceholder="Tarkennukset, lisähuomiot ym."
              disclaimer={
                idNotKnown ? 'Huom. syötä myös laitteen tarkemmat tiedot' : ''
              }
              name="additionalInfo"
              fallbackLabel="Palvelupyyntö"
              rows={5}
              required
            />
          </FieldWrapper>

          <Gap />

          {!!window.cordova && (
            <div>
              <FormLabel>Liitekuva</FormLabel>
              <PhotoAdder
                onPhotoChange={handlePhotoChange}
                photoData={
                  (values.attachmentInput?.length &&
                    values.attachmentInput[0]?.photo) ||
                  null
                }
              />
              <Gap height="24px" />
            </div>
          )}

          <FieldWrapper>
            <PhoneField
              required
              fallbackLabel="Puhelinnumero"
              name="contactPhone"
            />
          </FieldWrapper>

          <FieldWrapper>
            <CheckboxField fallbackLabel="Tartuntavaara" name="infectionRisk" />
          </FieldWrapper>

          <FieldWrapper>
            <CheckboxField
              fallbackLabel="Haluan saada tiedon huoltotilauksen valmistumisesta sähköpostiini"
              name="collectFeedback"
            />
          </FieldWrapper>

          {device?.transportable && (
            <FieldWrapper>
              <CheckboxField
                name="needsTransport"
                fallbackLabel="Tarvitsen laitteelle kuljetuksen"
              >
                {needsTransport && (
                  <>
                    <DeliveryTimeField name="deliveryTime" formId={formId} />
                    <Gap height="48px" />
                    <SpaceSelectField
                      fallbackLabel="Noutopaikka"
                      required
                      name="pickupSpace"
                      formId={formId}
                      collapseType="feedback"
                    />
                  </>
                )}
              </CheckboxField>
            </FieldWrapper>
          )}
        </>
      )}
    </>
  );
};

const FieldWrapper = props => <FieldContainer>{props.children}</FieldContainer>;

const FieldContainer = styled.div`
  position: relative;
  margin-bottom: 24px;
  max-width: var(--field-width);
`;

const Details = styled.div`
  margin-bottom: 48px;
  font-size: 16px;
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: auto auto;
  grid-gap: 16px;
  align-items: center;

  max-width: 480px;

  & > :nth-child(2n) {
    color: ${primaryColor};
  }
`;

const Detail = styled.div`
  word-break: break-word;
`;

const Instructions = styled(Button)`
  padding-left: 32px;
  padding-right: 32px;
`;

const Book = styled.img`
  margin-left: 12px;
`;

const ModalContent = styled.div`
  color: var(--primaryColor);
`;

const CantFind = styled.div`
  display: flex;
  align-items: center;
  color: var(--lighterBlue);
  margin-top: 24px;
  cursor: pointer;
`;

const CantFindIcon = styled.img`
  width: 16px;
  height: 16px;
  margin-right: 8px;
`;

const LocationContainer = styled.div`
  background-color: #f2f2f2;
  padding: 16px;
  margin-bottom: 40px;
  border-radius: 4px;
`;

export default ApplianceMaintenanceForm;
